import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import ProgressBar from "../../components/progressBar.jsx";
import SvgIcon from "../../assest/svg/svg.jsx";
import SkeletonLoader from "../../components/skeletonLoader.jsx";
import { useTranslation } from "react-i18next";

export default function Quiz() {

    const location = useLocation();
    const navigate = useNavigate();

    const quizId = location?.state?.quizId;
    const courseID = location?.state?.courseId;
    const moduleId = location?.state?.moduleId;
    const moduleTitle = location?.state?.moduleTitle;
    const moduleProgressId = location?.state?.moduleProgressId;

    const baseUrl = process.env.REACT_APP_LMS_APP_BASE_URL;
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    const profileData = JSON.parse(localStorage.getItem("profileData"));
    const userName = profileData?.name;

    const [quizData, setQuizData] = useState(null);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);


    const [timePerQuestion, setTimePerQuestion] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [resultData, setResultData] = useState(null);

    const [answers, setAnswers] = useState(() => {
        const saved = localStorage.getItem(`quizAnswers_${quizId}`);
        return saved ? JSON.parse(saved) : {};
    });

    const [currentStep, setCurrentStep] = useState(() => {
        const saved = localStorage.getItem(`currentStep_${quizId}`);
        return saved ? Number(saved) : 0;
    });

    const [timeLeft, setTimeLeft] = useState(0);
    const [quizStartedTime, setQuizStartedTime] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingTimer, setLoadingTimer] = useState(false);
    const [certificatePopup, setCertificatePopup] = useState(false);
    const [submitButtonClicked, setSubmitButtonClicked] = useState(false);
    // const [backNavigationPopup, setBackNavigationPopup] = useState(false);
    // const [submitConfirmPopup, setSubmitConfirmPopup] = useState(false);
    // const [shouldBlockNavigation, setShouldBlockNavigation] = useState(true);

    // Block navigation while quiz is in progress
    // const [quizInProgress, setQuizInProgress] = useState(false);

    const quizStartTime = useRef(Date.now());
    const questionStartTime = useRef(Date.now());
    const submitLock = useRef(false);
    const currentStepRef = useRef(currentStep);
    const { t } = useTranslation();
    const quizInProgress = useRef(false);

    // const [quizStarted, setQuizStarted] = useState(true);
    // usePreventLeave(quizStarted, "Are you sure you want to leave? Your quiz progress will be lost.")

    /** LOGOUT */
    // const handleSignOut = useCallback(() => {
    //     localStorage.removeItem("user");
    //     localStorage.removeItem("token");
    //     localStorage.removeItem("profileData");
    //     sessionStorage.removeItem("conversation");
    //     sessionStorage.removeItem("currentSessionId");
    //     sessionStorage.removeItem("sessionNumber");
    //     sessionStorage.removeItem("sessions");
    //     dispatch(logout());
    //     navigate("/login");
    // }, [dispatch, navigate]);

    /** 🕒 Record per question time */
    const recordTimeForCurrentQuestion = useCallback(() => {
        const now = Date.now();
        const timeTaken = Math.floor((now - questionStartTime.current) / 1000);
        setTimePerQuestion(prev => ({
            ...prev,
            [currentStep]: (prev[currentStep] || 0) + timeTaken,
        }));
        questionStartTime.current = now;
    }, [currentStep]);

    useEffect(() => {
        currentStepRef.current = currentStep;
    }, [currentStep]);

    /** Mark quiz as in progress when quiz data loads */
    useEffect(() => {
        if (quizData && !submitted) {
            quizInProgress.current = true;
        } else {
            quizInProgress.current = false;
        }
    }, [quizData, submitted]);

    /** Select Answer */
    const selectOption = useCallback((optionKey) => {
        if (submitted) return;
        setAnswers(prev => {
            const updated = {
                ...prev,
                [currentStepRef.current]: optionKey,
            };
            localStorage.setItem(`quizAnswers_${quizId}`, JSON.stringify(updated));
            return updated;
        });
    }, [quizId, submitted]);

    /** Next */
    const handleNext = useCallback(() => {
        recordTimeForCurrentQuestion();
        setCurrentStep(prev => {
            const next = prev + 1;
            if (quizData?.questions && next < quizData.questions.length) {
                localStorage.setItem(`currentStep_${quizId}`, next);
                return next;
            }
            return prev;
        });
    }, [quizData?.questions, quizId, recordTimeForCurrentQuestion]);

    /** Previous */
    const handlePrev = useCallback(() => {
        if (currentStep > 0) {
            recordTimeForCurrentQuestion();
            setCurrentStep(prev => {
                const previous = prev - 1;
                if (previous >= 0) {
                    localStorage.setItem(`currentStep_${quizId}`, previous);
                    return previous;
                }
                return prev;
            });
        }
    }, [quizId, recordTimeForCurrentQuestion, currentStep]);

    /** Fetch Quiz */
    const fetchQuiz = useCallback(async () => {
        try {
            setLoading(true);
            const res = await fetch(`${baseUrl}/user/courses/quiz/${quizId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                },
            });
            if (!res.ok) throw new Error("Failed to fetch quiz");
            const data = await res.json();
            setQuizData(data.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, quizId, token]);

    /** Fetch Quiz Start Time (to handle tab close) */
    const fetchQuizStartTime = useCallback(async () => {
        setLoadingTimer(true);
        try {
            const response = await axios.get(
                `${baseUrl}/module/get-module-all-data/${moduleId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = response.data?.data;
            const startedTime = data?.departments?.[0]?.module?.users?.[0]?.userModuleProgress?.[0]?.quizStartedAt;
            setQuizStartedTime(startedTime);
        } catch (error) {
            // if (error?.response?.status === 401) {
            //     handleSignOut()
            // }
            console.error(error);
            navigate('/');
        } finally {
            setLoadingTimer(false);
        }
    }, [baseUrl, moduleId, token, navigate]);

    /** Update Quiz Time Back to API */
    const updateQuizTime = useCallback(async () => {
        try {
            await axios.post(
                `${baseUrl}/module/update/user/progress`,
                {
                    moduleProgressId: moduleProgressId || "",
                    quizStartedAt: null,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (error) {
            console.log(error);
        }
    }, [baseUrl, moduleProgressId, token]);

    /** SUBMIT QUIZ */
    const handleSubmit = useCallback(async () => {
        recordTimeForCurrentQuestion();
        setSubmitting(true);
        setSubmitButtonClicked(true)
        try {
            const timeSpent = Math.floor((Date.now() - quizStartTime.current) / 1000);
            const questions = quizData?.questions || [];

            const formattedAnswers = questions.map((q, idx) => ({
                selectedAnswer: answers[idx] || "not_selected",
                timeTaken: timePerQuestion[idx] || 0,
            }));

            const payload = {
                courseId: courseID,
                moduleId: moduleId,
                quizId: quizData._id,
                timeSpent,
                answers: formattedAnswers,
            };

            const res = await fetch(`${baseUrl}/user/courses/quiz/submit`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                },
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error("Quiz submission failed");

            const submissionData = await res.json();
            const attemptId = submissionData?.data?.attempt?._id;

            if (!attemptId) throw new Error("Attempt ID not found");

            const resultRes = await fetch(`${baseUrl}/user/courses/quiz/results/${attemptId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                },
            });

            if (!resultRes.ok) throw new Error("Failed to fetch result");

            const result = await resultRes.json();
            const attempt = result?.data?.attempt;
            const quiz = result?.data?.quiz;

            if (attempt) {
                setResultData({
                    title: quiz?.title || quizData.title,
                    score: attempt.score || 0,
                    correctAnswers: attempt.correctAnswers || 0,
                    totalQuestions: attempt.totalQuestions || questions.length,
                    isPassed: attempt.isPassed,
                    passingScore: attempt.passingScore,
                    attemptId: attempt._id,
                    detailedResults: attempt?.answers,
                });
                setSubmitted(true);
                setSubmitting(false);
                localStorage.setItem("quizSubmitted", "true");
                localStorage.setItem("quizResultData", "true");
                localStorage.removeItem(`currentStep_${quizId}`);
                localStorage.removeItem(`quizAnswers_${quizId}`);
            }
        } catch (err) {
            // if (err?.response?.status === 401) handleSignOut();
            console.log(err);
        }
    }, [
        quizId,
        answers,
        timePerQuestion,
        quizData,
        courseID,
        moduleId,
        baseUrl,
        token,
        recordTimeForCurrentQuestion
    ]);

    useEffect(() => {
        if (!quizData?.module?.quizTime || loading) return;
        if (submitButtonClicked) return;
        let durationInSeconds = quizData.module.quizTime * 60;
        if (quizStartedTime) {
            const start = new Date(quizStartedTime);
            const now = new Date();
            const elapsed = Math.floor((now - start) / 1000);
            durationInSeconds = Math.max(durationInSeconds - elapsed, 0);
        }
        setTimeLeft(durationInSeconds);
        const interval = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    if (!submitLock.current) {
                        submitLock.current = true; // STOP FUTURE CALLS
                        handleSubmit();
                        updateQuizTime();
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [quizData, quizStartedTime, loading, submitButtonClicked, handleSubmit, updateQuizTime]);

    /** INIT FETCH */
    useEffect(() => {
        fetchQuiz();
    }, [fetchQuiz]);

    useEffect(() => {
        fetchQuizStartTime();
    }, [fetchQuizStartTime]);

    // useEffect(() => {
    //     // Determine if we should block navigation based on resultsData
    //     const shouldBlock = shouldBlockNavigation && !resultData;

    //     // Only add blocking if we should block navigation AND resultData is null
    //     if (!shouldBlock) return;

    //     // Add blocking state to prevent immediate back navigation
    //     const pushBlockingState = () => {
    //         window.history.pushState({ preventBack: true }, '');
    //     };

    //     // Initial blocking state
    //     pushBlockingState();

    //     const handlePopState = (event) => {
    //         // Only show popup and block if resultData is null
    //         if (!resultData) {
    //             // Show popup when back button is pressed
    //             setBackNavigationPopup(true);

    //             // Immediately push state again to prevent actual navigation
    //             setTimeout(() => {
    //                 if (shouldBlockNavigation && !resultData) {
    //                     pushBlockingState();
    //                 }
    //             }, 0);
    //         }
    //         // If resultsData exists, allow normal navigation
    //     };

    //     // Also handle page refresh/close
    //     const handleBeforeUnload = (e) => {
    //         if (shouldBlockNavigation && !resultData) {
    //             e.preventDefault();
    //             e.returnValue = 'Are you sure you want to leave? Changes may not be saved.';
    //             return e.returnValue;
    //         }
    //     };

    //     window.addEventListener('popstate', handlePopState);
    //     window.addEventListener('beforeunload', handleBeforeUnload);

    //     return () => {
    //         window.removeEventListener('popstate', handlePopState);
    //         window.removeEventListener('beforeunload', handleBeforeUnload);

    //         // Clean up the blocking state when component unmounts
    //         if (window.history.state?.preventBack && shouldBlockNavigation && !resultData) {
    //             window.history.back();
    //         }
    //     };
    // }, [shouldBlockNavigation, resultData]); // Add resultsData as dependency

    // const handleConfirmLeave = () => {
    //     setBackNavigationPopup(false);
    //     setShouldBlockNavigation(false);
    //     handleSubmit();
    //     updateQuizTime();
    // };

    // const handleCancelLeave = () => {
    //     setBackNavigationPopup(false);
    // };

    // useEffect(() => {
    //     if (!quizInProgress.current || resultData) return;

    //     const handleBeforeUnload = (e) => {
    //         console.log("Tab/Browser close or refresh detected - Auto submitting quiz");

    //         if (!submitLock.current) {
    //             submitLock.current = true;

    //             try {
    //                 const questions = quizData?.questions || [];
    //                 const formattedAnswers = questions.map((q, idx) => ({
    //                     selectedAnswer: answers[idx] || "not_selected",
    //                     timeTaken: timePerQuestion[idx] || 0,
    //                 }));

    //                 const timeSpent = Math.floor(
    //                     (Date.now() - quizStartTime.current) / 1000
    //                 );

    //                 const payload = {
    //                     courseId: courseID,
    //                     moduleId: moduleId,
    //                     quizId: quizData?._id || quizId,
    //                     timeSpent,
    //                     answers: formattedAnswers,
    //                 };

    //                 // ---- SUBMIT QUIZ ----
    //                 if (navigator?.sendBeacon) {
    //                     const blob = new Blob([JSON.stringify(payload)], {
    //                         type: "application/json",
    //                     });
    //                     navigator.sendBeacon(
    //                         `${baseUrl}/user/courses/quiz/submit`,
    //                         blob
    //                     );
    //                 }

    //                 if (typeof fetch === "function") {
    //                     fetch(`${baseUrl}/user/courses/quiz/submit`, {
    //                         method: "POST",
    //                         headers: {
    //                             "Content-Type": "application/json",
    //                             Authorization: token,
    //                         },
    //                         body: JSON.stringify(payload),
    //                         keepalive: true,
    //                     }).catch((err) =>
    //                         console.error("Error submitting quiz:", err)
    //                     );
    //                 }

    //                 // ---- LOCAL STORAGE UPDATES (ADDED) ----
    //                 localStorage.setItem("quizSubmitted", "true");
    //                 localStorage.setItem("quizResultData", "true");
    //                 localStorage.removeItem(`currentStep_${quizId}`);
    //                 localStorage.removeItem(`quizAnswers_${quizId}`);

    //                 // ---- CLEAR QUIZ START TIME ----
    //                 const progressPayload = {
    //                     moduleProgressId: moduleProgressId || "",
    //                     quizStartedAt: null,
    //                 };

    //                 if (navigator?.sendBeacon) {
    //                     const progressBlob = new Blob(
    //                         [JSON.stringify(progressPayload)],
    //                         { type: "application/json" }
    //                     );
    //                     navigator.sendBeacon(
    //                         `${baseUrl}/module/update/user/progress`,
    //                         progressBlob
    //                     );
    //                 }

    //                 if (typeof fetch === "function") {
    //                     fetch(`${baseUrl}/module/update/user/progress`, {
    //                         method: "POST",
    //                         headers: {
    //                             "Content-Type": "application/json",
    //                             Authorization: token,
    //                         },
    //                         body: JSON.stringify(progressPayload),
    //                         keepalive: true,
    //                     }).catch((err) =>
    //                         console.error("Error updating quiz time:", err)
    //                     );
    //                 }
    //             } catch (err) {
    //                 console.error("Error in beforeunload handler:", err);
    //             }
    //         }

    //         e.preventDefault();
    //         e.returnValue =
    //             "Quiz in progress. Your answers will be submitted if you leave.";
    //         return e.returnValue;
    //     };

    //     window.addEventListener("beforeunload", handleBeforeUnload);

    //     return () => {
    //         window.removeEventListener("beforeunload", handleBeforeUnload);
    //     };
    // }, [
    //     answers,
    //     timePerQuestion,
    //     quizData,
    //     baseUrl,
    //     courseID,
    //     moduleId,
    //     moduleProgressId,
    //     quizId,
    //     token,
    //     resultData,
    // ]);

    useEffect(() => {
        const quizSubmitted = localStorage.getItem("quizSubmitted");
        const resStored = localStorage.getItem("quizResultData");
        if (quizSubmitted === "true" && resStored === "true") {
            navigate("/lms/courses");
            localStorage.removeItem("quizSubmitted");
            localStorage.removeItem("quizResultData");
        }
    }, [navigate]);

    function getTodayDate() {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    async function HandleGenerateCertificate() {
        setCertificatePopup(true)
        try {
            const payload = {
                moduleId: moduleId,
                userName: userName,
                courseName: "English",
                score: resultData?.score,
                status: resultData?.isPassed ? "pass" : "fail",
                completionDate: getTodayDate(),
                cheifSignatureText: "Umer Aziz"
            };
            const response = await fetch(`${baseUrl}/certificate/generate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                },
                body: JSON.stringify(payload),
            });
            const result = await response.json();
            if (result.success) {
                setCertificatePopup(false)
                console.log('result comes', result);
                window.open(result.data.s3Url, "_blank");
            } else {
                alert("Certificate generation failed: " + result.message);
                setCertificatePopup(false)
            }
        } catch (err) {
            setCertificatePopup(false)
            console.error(err);
        }
    }

    if (error) return <div className="p-6 text-red-600 text-center">Error: {error}</div>;

    const questions = quizData?.questions || [];
    const total = questions?.length;
    const currentQuestion = questions[currentStep];

    return (
        <div className="flex h-auto">
            {/* {backNavigationPopup && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
                        aria-hidden="true"
                    />
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <div className="relative transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                            <button
                                onClick={handleCancelLeave}
                                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <div className="sm:flex sm:items-start">
                                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.786 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 className="text-lg font-semibold leading-6 text-gray-900">
                                        Leave this page?
                                    </h3>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            Are you sure you want to leave? Your changes may not be saved.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                                <button
                                    type="button"
                                    onClick={handleConfirmLeave}
                                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 sm:w-auto"
                                >
                                    Leave page
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancelLeave}
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )} */}
            {submitting === true && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="flex justify-center items-center py-20">
                        <div className="w-12 h-12 border-4 border-[#A73884] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                </div>
            )}
            <div className="w-full">
                {!submitted && (
                    <div className="min-h-[90vh] w-full flex items-center justify-center px-4 relative">
                        <div className="w-full max-w-6xl bg-white rounded-xl shadow-md p-4 sm:p-6 md:p-4">
                            {loading ? (
                                <SkeletonLoader count={1} className="mb-6 min-h-[80px] w-[100%]" style={{ borderRadius: '2rem' }} />
                            ) : (
                                <header className="flex items-center justify-between mb-2">
                                    <div>
                                        <h1 className="text-[22px] font-medium text-gray-900">
                                            Quiz - {quizData?.module?.title || "Quiz Title"}
                                        </h1>
                                        <p className="text-gray-600 mt-1">
                                            Question {currentStep + 1} of {total}
                                        </p>
                                    </div>
                                    <div>
                                        {submitted === false && resultData === null ? (
                                            loadingTimer ? (
                                                <div className="flex justify-center items-center">
                                                    <div className="w-8 h-8 border-4 border-[#A73884] border-t-transparent rounded-full animate-spin"></div>
                                                </div>
                                            ) : (
                                                <div className="relative w-20 h-20">
                                                    <svg className="w-20 h-20" viewBox="0 0 100 100">
                                                        <circle
                                                            cx="50"
                                                            cy="50"
                                                            r="45"
                                                            stroke="#E5E7EB"
                                                            strokeWidth="10"
                                                            fill="none"
                                                        />
                                                        <circle
                                                            cx="50"
                                                            cy="50"
                                                            r="45"
                                                            stroke="url(#gradient)"
                                                            strokeWidth="10"
                                                            fill="none"
                                                            strokeDasharray={2 * Math.PI * 45}
                                                            strokeDashoffset={2 * Math.PI * 45 * (1 - timeLeft / (quizData?.module?.quizTime * 60))}
                                                            strokeLinecap="round"
                                                            transform="rotate(-90 50 50)"
                                                            className="transition-all duration-500"
                                                        />
                                                        <defs>
                                                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                                <stop offset="0%" stopColor="#A73884" />
                                                                <stop offset="100%" stopColor="#803A85" />
                                                            </linearGradient>
                                                        </defs>
                                                    </svg>
                                                    {/* Timer Text */}
                                                    <h3 className="absolute inset-0 flex items-center justify-center text-md font-semibold text-[#6D6D6E]">
                                                        {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, "0")}
                                                    </h3>
                                                </div>
                                            )
                                        ) : null}
                                    </div>
                                </header>
                            )}
                            {loading ? (
                                <SkeletonLoader count={1} className="mb-6 min-h-[16px] w-[100%]" style={{ borderRadius: '2rem' }} />
                            ) : (
                                <ProgressBar progress={((currentStep + 1) / total) * 100} showProgress={false} m='my-8' />
                            )}
                            {loading ? (
                                <SkeletonLoader count={1} className="mb-6 min-h-[220px] w-[100%]" style={{ borderRadius: '2rem' }} />
                            ) : (
                                <div className="pb-8 mb-8">
                                    <div className="flex items-start gap-2 mb-4">
                                        <span className="text-black font-semibold text-lg">
                                            {currentStep + 1}:
                                        </span>
                                        <h3 className="text-[18px] font-semibold text-gray-900 flex-1 truncate w-40">
                                            {currentQuestion.question}
                                        </h3>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {Object.entries(currentQuestion.options).map(([key, label]) => {
                                                const selected = answers[currentStep] === key;
                                                return (
                                                    <button
                                                        key={key}
                                                        onClick={() => selectOption(key)}
                                                        className={`w-full px-3 py-3 min-h-16 max-h-full rounded-md border-2 text-left transition-all duration-300 cursor-pointer group ${selected
                                                            ? "bg-gradient-to-r from-[#A73884] to-[#803A85] border-[#A73884]"
                                                            : "border-gray-200 hover:border-purple-200 hover:bg-purple-50/50"
                                                            }`}
                                                    >
                                                        <span
                                                            className={`text-[14px] w-full block overflow-hidden whitespace-nowrap truncate ${selected ? "text-white" : "text-gray-900"
                                                                }`}
                                                        >
                                                            {label}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {loading ? (
                                <SkeletonLoader count={1} className="mb-6 min-h-[64px] w-[100%]" style={{ borderRadius: '2rem' }} />
                            ) : (
                                <div className="pb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                                    {/* Previous Button */}
                                    <button
                                        onClick={() => {
                                            if (currentStep > 0) {
                                                handlePrev();
                                            }
                                        }}
                                        disabled={currentStep === 0}
                                        className={`px-6 py-2 rounded-full font-medium transition-all duration-300 
                whitespace-nowrap w-full sm:w-auto
                ${currentStep === 0
                                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                : "border-2 border-gray-100 text-gray-700 hover:bg-gray-200"
                                            }`}
                                    >
                                        <i className="ri-arrow-left-line mr-2" />
                                        {t('Previous')}

                                    </button>

                                    {/* Number Pagination */}
                                    <div className="flex flex-wrap justify-center gap-2 order-3 sm:order-none">
                                        {(() => {
                                            const visibleCount = 5;
                                            const groupStart = Math.floor(currentStep / visibleCount) * visibleCount;
                                            const groupEnd = Math.min(groupStart + visibleCount, total);

                                            return Array.from({ length: groupEnd - groupStart }, (_, i) => {
                                                const idx = groupStart + i;
                                                const isDisabled = !submitted && idx !== currentStep;

                                                return (
                                                    <button
                                                        key={idx}
                                                        onClick={() => !isDisabled && setCurrentStep(idx)}
                                                        disabled={isDisabled}
                                                        className={`
                                w-10 h-10 rounded-full font-medium transition-all duration-300
                                ${idx === currentStep
                                                                ? "bg-[#B03B82] text-white shadow-lg"
                                                                : isDisabled
                                                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                            }
                            `}
                                                    >
                                                        {idx + 1}
                                                    </button>
                                                );
                                            });
                                        })()}
                                    </div>

                                    {/* Next / Submit Button */}
                                    {currentStep < total - 1 ? (
                                        <button
                                            onClick={handleNext}
                                            disabled={!answers[currentStep]}
                                            className={`
                    px-6 py-2 rounded-full font-medium text-white bg-[#B03B82]
                    hover:shadow-lg hover:shadow-purple-300/50 transition-all duration-300
                    w-full sm:w-auto
                    ${!answers[currentStep] ? "opacity-60 cursor-not-allowed" : ""}
                `}
                                        >
                                            {t('Next')}
                                            <i className="ri-arrow-right-line ml-2" />
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => {
                                                handleSubmit();
                                                updateQuizTime();
                                            }}
                                            disabled={Object.keys(answers).length < total || submitting}
                                            className={`
                    px-6 py-3 rounded-xl font-medium text-white
                    bg-gradient-to-r from-[#A73884] to-[#803A85]
                    hover:shadow-lg hover:shadow-purple-300/50 transition-all duration-300
                    flex items-center gap-2
                    w-full sm:w-auto
                    ${Object.keys(answers).length < total || submitting
                                                    ? "opacity-60 cursor-not-allowed"
                                                    : ""}
                `}
                                        >
                                            {submitting ? (
                                                <>
                                                    <svg
                                                        className="animate-spin h-5 w-5 text-white"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        />
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                        />
                                                    </svg>
                                                    Submitting...
                                                </>
                                            ) : "Submit Quiz"}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* <div className="absolute bottom-0 right-0 hidden md:flex flex-col items-center space-y-4">
                            <div className="w-[12rem] h-[18rem]">
                                <CharacterDisplay loading={false} />
                            </div>
                        </div> */}
                    </div>
                )}
                {submitted && (
                    <div className="max-w-7xl mx-auto">
                        {/* Summary Card */}
                        <div className="bg-gradient-to-r from-[#A73884] to-[#803A85] border border-gray-200/50 rounded-2xl p-8 mb-6 shadow-lg hover:shadow-xl transition-shadow duration-300 relative">
                            {/* <div className="absolute top-4 right-4">
                                <img
                                    alt="Learning Character"
                                    className="w-24 h-24 object-contain"
                                    src="https://static.readdy.ai/image/f7a3d5ae10bab158f7003a8471295347/bc4c0da2f776773bbeca808400bb1c90.png"
                                />
                            </div> */}

                            {/* Score Circle */}
                            <div className="text-center mb-8">
                                <div className="relative inline-flex items-center justify-center w-32 h-32 rounded-full bg-white mb-4 animate-pulse-slow">
                                    <span className="text-4xl font-medium text-[#B03B82]">
                                        {resultData?.score?.toFixed(0)}%
                                    </span>
                                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#c5c5c5] to-[#ececec] opacity-20 animate-ping" />
                                </div>
                                <h2 className="text-4xl font-medium text-white mb-2">
                                    {resultData?.isPassed ? t("Congratulations!") : t("Keep Learning!")}
                                </h2>
                                <p className="text-xl text-white">
                                    {resultData?.isPassed
                                        ? `${t("You scored")} ${resultData?.score?.toFixed(0)}%! ${t("Great job")}!`
                                        : `${t("You scored")} ${resultData?.score?.toFixed(0)}%. ${t("Try again to improve")}!`}
                                </p>
                            </div>

                            {/* Score Stats */}
                            <div className="
    grid 
    grid-cols-1 
    sm:grid-cols-2 
    lg:grid-cols-4 
    gap-6 
    mb-8 
    bg-[#F4F6F6] 
    rounded-xl 
    p-4 sm:p-6
">
                                {/* Correct */}
                                <div className="p-4 flex flex-col justify-center items-center">
                                    <div className="mb-3 flex items-center justify-center border-2 p-2 bg-white rounded-full">
                                        <div className="flex items-center justify-center bg-[#3BB873] w-14 h-14 sm:w-[70px] sm:h-[70px] rounded-full shrink-0">
                                            <SvgIcon name="check" size={40} />
                                        </div>
                                    </div>
                                    <p className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                                        {resultData?.correctAnswers}
                                    </p>
                                    <p className="text-sm sm:text-md text-gray-600">{t("Correct")}</p>
                                </div>

                                {/* Incorrect */}
                                <div className="p-4 flex flex-col justify-center items-center">
                                    <div className="mb-3 flex items-center justify-center border-2 p-2 bg-white rounded-full">
                                        <div className="flex items-center justify-center bg-[#E5646E] w-14 h-14 sm:w-[70px] sm:h-[70px] rounded-full shrink-0">
                                            <SvgIcon name="cross" size={40} />
                                        </div>
                                    </div>
                                    <p className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                                        {resultData?.totalQuestions - resultData?.correctAnswers}
                                    </p>
                                    <p className="text-sm sm:text-md text-gray-600">{t("Incorrect")}</p>
                                </div>

                                {/* Score */}
                                <div className="p-4 flex flex-col justify-center items-center">
                                    <div className="mb-3 flex items-center justify-center border-2 p-2 bg-white rounded-full">
                                        <div className="flex items-center justify-center bg-[#B03B82] w-14 h-14 sm:w-[70px] sm:h-[70px] rounded-full shrink-0">
                                            <SvgIcon name="percentage" size={40} />
                                        </div>
                                    </div>
                                    <p className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                                        {resultData?.score?.toFixed(0)}%
                                    </p>
                                    <p className="text-sm sm:text-md text-gray-600">{t("Score")}</p>
                                </div>

                                {/* Status */}
                                <div className="p-4 flex flex-col justify-center items-center">
                                    <div className="
            w-14 h-14 sm:w-16 sm:h-16 
            rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg 
            bg-gradient-to-r from-[#A73884] to-[#803A85]
        ">
                                        {resultData?.isPassed ? (
                                            <SvgIcon name="passed" size={40} />
                                        ) : (
                                            <SvgIcon name="retry" size={40} />
                                        )}
                                    </div>

                                    <p className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                                        {resultData?.isPassed ? "Passed" : "Retry"}
                                    </p>
                                    <p className="text-sm sm:text-md text-gray-600">{t("Status")}</p>
                                </div>
                            </div>

                            {/* Buttons */}
                            <div className="flex items-center justify-center gap-4 mb-8">
                                {/* <button
                                    //   onClick={handleRetake}
                                    className="px-8 py-4 bg-gradient-to-r from-[#A73884] to-[#803A85] text-white rounded-xl font-bold hover:shadow-lg hover:shadow-purple-300/50 transition-all duration-300 whitespace-nowrap cursor-pointer group"
                                >
                                    <i className="ri-refresh-line mr-2 group-hover:rotate-180 transition-transform duration-300" />
                                    Retake Quiz
                                </button> */}
                                {resultData?.isPassed ? (
                                    certificatePopup ? (
                                        <div className="w-8 h-8 border-4 border-[#A73884] border-t-transparent rounded-full animate-spin"></div>
                                    ) : <button
                                        onClick={HandleGenerateCertificate}
                                        className="px-8 py-4 bg-gradient-to-r from-[#A73884] to-[#803A85] text-white rounded-xl font-bold hover:shadow-lg hover:shadow-purple-300/50 transition-all duration-300 whitespace-nowrap cursor-pointer group flex items-center"
                                    >
                                        <i className="ri-medal-line mr-2 text-xl group-hover:scale-110 transition-transform duration-300" />
                                        {t("View Certificate")}
                                    </button>
                                ) : null}
                            </div>
                        </div>

                        <div className="text-center mb-10">
                            <button
                                onClick={() => navigate(`/lms/course?module=${moduleTitle}`, { state: { moduleId: moduleId } })}
                                className="px-8 py-2 border border-gray-200 text-gray-700 rounded-full font-medium hover:bg-gray-200 transition-all duration-300 whitespace-nowrap cursor-pointer"
                            >
                                {/* <i className="ri-home-line mr-2" /> */}
                                {t("Back to Module")}
                            </button>
                        </div>

                        {/* Detailed Review Section */}
                        <div className="bg-[#F4F6F8] backdrop-blur-xl rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <div className="px-6 py-5 bg-gray-50/50">
                                <h3 className="text-xl font-medium text-gray-900">{t("Detailed Review")}</h3>
                            </div>
                            <div className="p-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                                    {resultData?.detailedResults.map((q, idx) => {
                                        const userWasCorrect = q.isCorrect;
                                        const selectedLabel = `${q.selectedOption}. ${q.selectedAnswer}`;
                                        const correctLabel = `${q.correctOption}. ${q.correctAnswer}`;
                                        return (
                                            <div
                                                key={q._id}
                                                className="border border-gray-200/50 rounded-xl p-6 bg-white shadow-sm hover:shadow-md transition-all duration-300"
                                            >
                                                <div className="flex items-start gap-4">

                                                    {/* Number Circle */}
                                                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-purple-100 border border-purple-200">
                                                        <span className="text-sm font-bold text-gray-900">{idx + 1}</span>
                                                    </div>

                                                    {/* MAIN CONTENT (must have min-w-0!!) */}
                                                    <div className="flex-1 min-w-0">

                                                        {/* Question */}


                                                        <div class="group relative cursor-pointer">
                                                            <h4
                                                                className="text-lg font-semibold text-gray-900 mb-4 truncate"
                                                                style={{ maxWidth: "100%" }}
                                                            >
                                                                {q.question}
                                                            </h4>
                                                            {/* <div
                                                                className="absolute left-1/2 -translate-x-1/2 opacity-0 invisible group-hover:opacity-100
           group-hover:visible transition-all duration-200 z-50 whitespace-normal break-words
           rounded-lg bg-gray-700 py-1.5 px-3 text-sm text-white w-[500px] font-medium">
                                                                {q.question}
                                                            </div> */}
                                                        </div>


                                                        {/* Selected Answer */}
                                                        {q.selectedOption === "not_selected" ? (
                                                            <div className="p-4 rounded-lg border mb-3 border-red-500 bg-red-50 text-red-800 break-words whitespace-normal">
                                                                <div className="flex items-start gap-3">
                                                                    <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center border-2 border-red-500">
                                                                        <i className="ri-close-line text-xs" />
                                                                    </div>
                                                                    <span className="font-medium break-words whitespace-normal">
                                                                        {t("Option not selected")}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div class="group relative cursor-pointer">
                                                                <div
                                                                    className={`p-4 rounded-lg border mb-3 break-words whitespace-normal ${userWasCorrect
                                                                        ? "border-green-500 bg-green-50 text-green-800"
                                                                        : "border-red-500 bg-red-50 text-red-800"
                                                                        }`}
                                                                >
                                                                    <div className="flex items-start gap-3">
                                                                        <div
                                                                            className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center border-2 ${userWasCorrect ? "border-green-500" : "border-red-500"
                                                                                }`}
                                                                        >
                                                                            <i className={`ri-${userWasCorrect ? "check" : "close"}-line text-xs`} />
                                                                        </div>
                                                                        <span className="font-medium truncate">
                                                                            {selectedLabel}
                                                                        </span>
                                                                        {/* <div
                                                                            className="absolute left-1/2 -translate-x-1/2 mt-14 opacity-0 invisible group-hover:opacity-100
           group-hover:visible transition-all duration-200 z-50 whitespace-normal break-words
           rounded-lg bg-gray-700 py-1.5 px-3 text-sm text-white w-[500px] font-medium">
                                                                            {selectedLabel}
                                                                        </div> */}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Correct Answer */}
                                                        {!userWasCorrect && (
                                                            <div class="group relative cursor-pointer">
                                                                <div className="p-4 rounded-lg border border-green-500 bg-green-50 text-green-800 mb-3 break-words whitespace-normal">
                                                                    <p className="text-sm font-semibold truncate">
                                                                        {t("Correct Answer")}: {correctLabel}
                                                                    </p>
                                                                    {/* <div
                                                                        className="absolute left-1/2 -translate-x-1/2 mt-10 opacity-0 invisible group-hover:opacity-100
           group-hover:visible transition-all duration-200 z-50 whitespace-normal break-words
           rounded-lg bg-gray-700 py-1.5 px-3 text-sm text-white w-[500px] font-medium">
                                                                        {correctLabel}
                                                                    </div> */}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Explanation */}
                                                        {q.explanation && (
                                                            <details className="mt-3 cursor-pointer">
                                                                <summary className="text-sm font-medium text-[#898A8A] hover:underline break-words whitespace-normal">
                                                                    {t("Show Explanation")}
                                                                </summary>
                                                                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                    <p className="text-gray-700 text-sm leading-relaxed break-words whitespace-normal">
                                                                        {q.explanation}
                                                                    </p>
                                                                </div>
                                                            </details>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* CHARACTER / SIDE VIDEO */}
            {/* {submitted && (
                <div className="gap-36 flex w-[35rem] h-[40rem] flex-col">
                    <QuizeCharacterVideo video1={Reading_in} video2={Reading_loop} />
                </div>
            )} */}
        </div>
    );
}